import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { dietAPI, progressAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import MacroDashboard from './components/MacroDashboard';
import MealSection, { type DietEntry } from './components/MealSection';
import type { DietLog } from '../../types';

type View = 'daily' | 'plan';

function computeMacroTargets(plan: any) {
  const cals = plan?.dailyCalories || 0;
  const split = plan?.macroSplit;
  if (!cals || !split) return { calories: cals || 0, protein: 0, carbs: 0, fat: 0 };
  // Convert % to grams: protein & carbs = 4 kcal/g, fat = 9 kcal/g
  return {
    calories: cals,
    protein: Math.round((cals * (split.protein / 100)) / 4),
    carbs: Math.round((cals * (split.carbs / 100)) / 4),
    fat: Math.round((cals * (split.fat / 100)) / 9),
  };
}

function computeLogged(entries: DietEntry[]) {
  return entries.reduce(
    (acc, e) => ({
      calories: acc.calories + (e.calories || 0),
      protein: acc.protein + (e.protein || 0),
      carbs: acc.carbs + (e.carbs || 0),
      fat: acc.fat + (e.fat || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );
}

function getToday() {
  return new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
}

export default function DietPlans() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState<View>('daily');
  const [plan, setPlan] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Today's diet log state
  const [entries, setEntries] = useState<DietEntry[]>([]);
  const [todayLogId, setTodayLogId] = useState<string | null>(null);
  const [weeklyLogs, setWeeklyLogs] = useState<DietLog[]>([]);
  const [saving, setSaving] = useState(false);

  // Plan view: open days
  const [openDays, setOpenDays] = useState<Set<number>>(new Set([0]));

  const saveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (user?._id) {
      loadAll();
    }
  }, [user]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [planRes, logsRes] = await Promise.all([
        dietAPI.getDietPlans() as Promise<{ success?: boolean; plans?: any[] }>,
        progressAPI.getDietLogs(user!._id) as Promise<any>,
      ]);

      if (planRes?.success && planRes?.plans?.length) {
        setPlan(planRes.plans[0]);
      }

      const allLogs: DietLog[] = logsRes?.logs || [];
      setWeeklyLogs(allLogs);

      const today = getToday();
      const todayLog = allLogs.find((l) => l.date === today);
      if (todayLog) {
        setTodayLogId(todayLog._id || todayLog.id || null);
        setEntries((todayLog.entries || []) as DietEntry[]);
      }
    } catch (err: any) {
      console.error('Load diet data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const persistEntries = useCallback(async (newEntries: DietEntry[]) => {
    if (!user?._id) return;
    setSaving(true);
    try {
      const totals = computeLogged(newEntries);
      const payload = {
        date: getToday(),
        entries: newEntries,
        totalCalories: totals.calories,
        totalProtein: totals.protein,
        totalCarbs: totals.carbs,
        totalFat: totals.fat,
      };
      if (todayLogId) {
        await progressAPI.updateDietLog(user._id, todayLogId, payload);
      } else {
        const res = await progressAPI.createDietLog(user._id, payload) as any;
        if (res?.log?._id) setTodayLogId(res.log._id);
        else if (res?.log?.id) setTodayLogId(res.log.id);
      }
    } catch (err) {
      console.error('Save diet log error:', err);
    } finally {
      setSaving(false);
    }
  }, [user, todayLogId]);

  // Debounced auto-save
  const scheduleAutoSave = (newEntries: DietEntry[]) => {
    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = setTimeout(() => persistEntries(newEntries), 500);
  };

  const handleAddEntry = (entry: DietEntry) => {
    const updated = [...entries, entry];
    setEntries(updated);
    scheduleAutoSave(updated);
  };

  const handleRemoveEntry = (entryId: string) => {
    const updated = entries.filter(e => (e.id || e.name) !== entryId);
    setEntries(updated);
    scheduleAutoSave(updated);
  };

  const handleGenerate = async () => {
    if (!user?.weight || !user?.height || !user?.age) {
      setError(t('dietPlans.errors.incompleteData'));
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const result = await dietAPI.generateDietPlan() as { success?: boolean; plan?: any; message?: string };
      if (result?.success && result?.plan) {
        setPlan(result.plan);
        setOpenDays(new Set([0]));
      } else {
        setError(result?.message || 'Failed to generate plan');
      }
    } catch (err: any) {
      setError(err.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const entriesByMeal = (mealType: string) =>
    entries.filter(e => (e.mealType || 'snack') === mealType);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const targets = computeMacroTargets(plan);
  const logged = computeLogged(entries);
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-bold text-gray-900">{t('dietPlans.title')}</h1>
        <div className="flex items-center gap-2">
          {saving && <span className="text-xs text-gray-400">Saving...</span>}
          <button
            onClick={() => setView(v => v === 'daily' ? 'plan' : 'daily')}
            className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {view === 'daily' ? 'View Plan' : 'Today\'s Log'}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4">{error}</div>}

      {/* ── DAILY VIEW ── */}
      {view === 'daily' && (
        <>
          <p className="text-sm text-gray-500 mb-4">{today}</p>

          {plan ? (
            <MacroDashboard targets={targets} logged={logged} weeklyLogs={weeklyLogs} />
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4 text-sm text-yellow-800">
              No diet plan yet — macros targets will be empty.{' '}
              <button onClick={() => setView('plan')} className="underline font-medium">Generate a plan</button>.
            </div>
          )}

          <div className="space-y-3">
            {(['breakfast', 'lunch', 'dinner', 'snack'] as const).map(meal => (
              <MealSection
                key={meal}
                mealType={meal}
                entries={entriesByMeal(meal)}
                onAddEntry={handleAddEntry}
                onRemoveEntry={handleRemoveEntry}
              />
            ))}
          </div>

          <div className="mt-5 bg-blue-50 rounded-xl p-4">
            <p className="text-blue-800 text-sm">
              {t('dietPlans.modifyPlanTitle')}{' '}
              <button onClick={() => navigate('/ai-assistant')} className="font-semibold underline">
                {t('dietPlans.goToAI')}
              </button>
            </p>
          </div>
        </>
      )}

      {/* ── PLAN VIEW ── */}
      {view === 'plan' && (
        <>
          {!plan ? (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              {(!user?.weight || !user?.height || !user?.age) ? (
                <>
                  <p className="text-yellow-800 mb-4">{t('dietPlans.fillDataFirst')}</p>
                  <button onClick={() => navigate('/')} className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                    {t('common.goToUserData')}
                  </button>
                </>
              ) : (
                <>
                  <div className="text-5xl mb-4">🥗</div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No diet plan yet</h3>
                  <p className="text-gray-500 mb-6">{t('dietPlans.readyToCreate')}</p>
                  <button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="px-8 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {generating ? t('dietPlans.generating') : t('dietPlans.generate')}
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{plan.title}</h2>
                  {plan.description && <p className="text-sm text-gray-500 mt-0.5">{plan.description}</p>}
                </div>
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {generating ? t('dietPlans.generating') : t('dietPlans.generateNew')}
                </button>
              </div>

              {/* Macro summary badges */}
              {plan.dailyCalories && (
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {[
                    { label: 'Calories', value: plan.dailyCalories, unit: 'kcal', color: 'text-indigo-600' },
                    { label: 'Protein', value: `${plan.macroSplit?.protein}%`, unit: '', color: 'text-blue-600' },
                    { label: 'Carbs', value: `${plan.macroSplit?.carbs}%`, unit: '', color: 'text-yellow-600' },
                    { label: 'Fat', value: `${plan.macroSplit?.fat}%`, unit: '', color: 'text-red-500' },
                  ].map(({ label, value, unit, color }) => (
                    <div key={label} className="bg-white rounded-xl shadow-sm p-3 text-center">
                      <div className={`text-xl font-bold ${color}`}>{value}{unit}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Day cards */}
              <div className="space-y-3">
                {plan.schedule?.map((day: any, idx: number) => {
                  const isOpen = openDays.has(idx);
                  return (
                    <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                      <button
                        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                        onClick={() => setOpenDays(prev => {
                          const next = new Set(prev);
                          next.has(idx) ? next.delete(idx) : next.add(idx);
                          return next;
                        })}
                      >
                        <span className="font-semibold text-gray-900">{day.day}</span>
                        {isOpen ? <ChevronUpIcon className="w-4 h-4 text-gray-400" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
                      </button>
                      {isOpen && (
                        <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-4">
                          {day.meals?.map((meal: any, mIdx: number) => (
                            <div key={mIdx}>
                              <h4 className="font-semibold text-gray-700 mb-1.5 text-sm">
                                {meal.name}
                                {meal.time && <span className="text-gray-400 font-normal"> · {meal.time}</span>}
                              </h4>
                              <div className="space-y-1">
                                {meal.foods?.map((food: any, fIdx: number) => (
                                  <div key={fIdx} className="flex justify-between items-center text-sm py-1.5 border-b border-gray-50 last:border-0">
                                    <span className="text-gray-700">{food.name} <span className="text-gray-400 text-xs">({food.portion})</span></span>
                                    <span className="text-gray-500 text-xs">{food.calories} kcal</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 bg-blue-50 rounded-xl p-4">
                <p className="text-blue-800 text-sm">
                  {t('dietPlans.modifyPlanTitle')}{' '}
                  <button onClick={() => navigate('/ai-assistant')} className="font-semibold underline">
                    {t('dietPlans.goToAI')}
                  </button>
                </p>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
